import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { PlusCircle, Search, AlertTriangle, XCircle, Trash2, RotateCw, Users, Package, History, CheckCircle, Archive, Edit2, TrendingUp, DollarSign } from 'lucide-react';
import Swal from 'sweetalert2';
import AlmacenInsumos from './AlmacenInsumos';
import { logAuditAction } from '../api/auditService';
import MarginModal from '../components/specific/Inventory/MarginModal';
import { fetchSistemaInventory } from '../api/sistemaBridge';

import { parseMoney } from '../utils/money';

const InventoryDashboard = () => {
    const UMBRAL_RENTABILIDAD = 0.15; // 15% en formato decimal
    const [activeTab, setActiveTab] = useState('almacen'); // 'almacen', 'stock', 'renovadas', 'bitacora', 'proveedores'
    const { currentUser } = useAuth();
    const userRole = currentUser?.role?.toUpperCase() || '';
    const isATC = userRole === 'ATENCIÓN AL CLIENTE' || userRole.includes('ATENCIÓN AL CLIENTE');
    const isAdminOrGerente = ['ADMIN', 'GERENTE'].includes(userRole);

    const [proveedores, setProveedores] = useState([]);
    const [cuentas, setCuentas] = useState([]);
    const [configServicios, setConfigServicios] = useState([]);
    const [stockAlmacen, setStockAlmacen] = useState([]);
    const [cuentasRenovadas, setCuentasRenovadas] = useState([]);
    const [sistemaInventory, setSistemaInventory] = useState({ stock_perfiles: [], stock_cuentas: [], cuentas: [] });

    // Filters
    const [filtroServicio, setFiltroServicio] = useState('Todos');
    const [filtroEstado, setFiltroEstado] = useState('Todos');
    const [filtroNotas, setFiltroNotas] = useState('Todos');
    const [filtroProvServicio, setFiltroProvServicio] = useState('');
    
    // Inline Edit State
    const [editingId, setEditingId] = useState(null);
    const [editingValue, setEditingValue] = useState(0);

    // Modals
    const [showCuentaModal, setShowCuentaModal] = useState(false);
    const [showProvModal, setShowProvModal] = useState(false);
    const [showMarginModal, setShowMarginModal] = useState(false);
    const [selectedMarginData, setSelectedMarginData] = useState(null);

    // Form State Proveedor
    const [provNombre, setProvNombre] = useState('');
    const [provAlias, setProvAlias] = useState('');
    const [provContacto, setProvContacto] = useState('');

    // Form State Bitacora
    const [bitacora, setBitacora] = useState([]);
    const [bitacoraAccion, setBitacoraAccion] = useState('Cuenta Creada');
    const [bitacoraPlataforma, setBitacoraPlataforma] = useState('');
    const [bitacoraCorreo, setBitacoraCorreo] = useState('');
    const [bitacoraNotas, setBitacoraNotas] = useState('');
    // Form State Cuenta
    const [cuentaProvId, setCuentaProvId] = useState('');
    const [cuentaServicio, setCuentaServicio] = useState('Netflix');
    const [cuentaCorreo, setCuentaCorreo] = useState('');
    const [cuentaVencimiento, setCuentaVencimiento] = useState('');
    const [cuentaTipoProducto, setCuentaTipoProducto] = useState('Cuenta Completa');
    const [cuentaEstadoSalud, setCuentaEstadoSalud] = useState('Disponible');
    const [cuentaCostoCompra, setCuentaCostoCompra] = useState('');
    const [cuentaCostoProveedor, setCuentaCostoProveedor] = useState('');
    const [cuentaPrecioDistribuidor, setCuentaPrecioDistribuidor] = useState('');
    const [cuentaPrecioRevendedor, setCuentaPrecioRevendedor] = useState('');
    const [cuentaPrecioClienteFinal, setCuentaPrecioClienteFinal] = useState('');
    const [cuentaNotas, setCuentaNotas] = useState('');
    const [editingCuenta, setEditingCuenta] = useState(null);

    const serviciosList = ['Netflix', 'Max', 'Prime Video', 'Disney+', 'Spotify', 'Apple TV', 'Crunchyroll', 'Paramount+'];
    const estadosCuenta = ['Disponible', 'Asignada', 'Pausada / Reporte', 'Caída', 'Vencida'];

    useEffect(() => {
        fetchSistemaInventory().then(setSistemaInventory).catch(error => console.error('No se pudo sincronizar inventario Sistema:', error));
        const qProv = query(collection(db, 'proveedores'), orderBy('nombre', 'asc'));
        const unsubProv = onSnapshot(qProv, (snapshot) => {
            setProveedores(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const qCuentas = query(collection(db, 'inventario_cuentas'), orderBy('fecha_vencimiento', 'asc'));
        const unsubCuentas = onSnapshot(qCuentas, (snapshot) => {
            setCuentas(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const qServ = query(collection(db, 'config_servicios'));
        const unsubServ = onSnapshot(qServ, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            data.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
            setConfigServicios(data);
        });

        const qStock = query(collection(db, 'almacen_stock'));
        const unsubStock = onSnapshot(qStock, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setStockAlmacen(data);
        });

        const qRenovaciones = query(collection(db, 'cuentas_renovadas'), orderBy('fecha_renovacion', 'desc'));
        const unsubRenovaciones = onSnapshot(qRenovaciones, (snapshot) => {
            setCuentasRenovadas(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const qBitacora = query(collection(db, 'inventario_bitacora'), orderBy('fecha', 'desc'));
        const unsubBitacora = onSnapshot(qBitacora, (snapshot) => {
            setBitacora(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => { unsubProv(); unsubCuentas(); unsubServ(); unsubStock(); unsubRenovaciones(); unsubBitacora(); };
    }, []);
    const generarReporteRenovaciones = () => {
        const now = new Date();
        const past30Days = new Date();
        past30Days.setDate(now.getDate() - 30);
        
        const next3Days = new Date();
        next3Days.setDate(now.getDate() + 3);

        const criticas = cuentas.filter(c => {
            if (!c.fecha_vencimiento) return false;
            const fVenc = new Date(c.fecha_vencimiento);
            return fVenc >= past30Days && fVenc <= next3Days;
        });

        // Ordenamiento Cronológico
        criticas.sort((a, b) => new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento));

        const gruposProcesados = [];
        criticas.forEach(c => {
            const fVenc = new Date(c.fecha_vencimiento);
            const fStr = fVenc.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
            
            const prov = proveedores.find(p => p.id === c.proveedor_id);
            const provName = prov ? (prov.alias || prov.nombre) : (c.proveedor || 'N/A');

            const match = gruposProcesados.find(g => g.servicio === c.servicio.toUpperCase() && g.proveedor === provName && g.vencimiento === fStr);
            if (match) {
                if (c.correo_cuenta) match.correos.push(c.correo_cuenta);
            } else {
                gruposProcesados.push({
                    servicio: c.servicio.toUpperCase(),
                    proveedor: provName,
                    vencimiento: fStr,
                    correos: c.correo_cuenta ? [c.correo_cuenta] : []
                });
            }
        });

        let contenido = `Hoprin+ | Informe de Renovaciones Críticas | ${new Date().toLocaleString('es-ES')}\n\n`;
        
        if (gruposProcesados.length === 0) {
            contenido += "No hay cuentas por renovar en este período.\n";
        } else {
            gruposProcesados.forEach(g => {
                contenido += `[${g.servicio}]\n`;
                contenido += `Proveedor: ${g.proveedor}\n`;
                contenido += `Vencimiento: ${g.vencimiento}\n\n`;
                g.correos.forEach(email => {
                    contenido += `${email}\n`;
                });
                contenido += `\n`;
            });
        }

        const blob = new Blob([contenido], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Informe_Renovaciones_${new Date().toISOString().slice(0, 10)}.txt`);
        link.click();
        URL.revokeObjectURL(url);
    };

    const generarReporteCompras = () => {
        const necesarias = catalogoAlmacen.filter(item => item.unidades <= item.punto_de_reorden);
        
        let contenido = `Hoprin+ | Informe de Compras Necesarias | ${new Date().toLocaleString('es-ES')}\n\n`;
        
        if (necesarias.length === 0) {
            contenido += "Todos los servicios cuentan con stock óptimo.\n";
        } else {
            necesarias.forEach(item => {
                const deficit = item.punto_de_reorden - item.unidades;
                contenido += `Servicio: ${item.plataforma} | Stock Actual: ${item.unidades} | Mínimo para Reorden: ${item.punto_de_reorden} | FALTANTE: ${deficit}\n`;
            });
        }

        const blob = new Blob([contenido], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Informe_Compras_${new Date().toISOString().slice(0, 10)}.txt`);
        link.click();
        URL.revokeObjectURL(url);
    };
    // --- CUENTAS LOGIC ---
    const handleSaveCuenta = async (e) => {
        e.preventDefault();
        if (!cuentaProvId) return Swal.fire('Error', 'Selecciona un proveedor', 'error');
        try {
            const docPayload = {
                proveedor_id: cuentaProvId,
                servicio: cuentaServicio,
                correo_cuenta: cuentaCorreo,
                perfiles_disponibles: 1,
                fecha_vencimiento: new Date(cuentaVencimiento).getTime(),
                estado: cuentaEstadoSalud,
                tipo_producto: cuentaTipoProducto,
                costo_proveedor: Number(cuentaCostoProveedor) || 0,
                precio_distribuidor: Number(cuentaPrecioDistribuidor) || 0,
                precio_revendedor: Number(cuentaPrecioRevendedor) || 0,
                precio_cliente_final: Number(cuentaPrecioClienteFinal) || 0,
                notas: cuentaNotas
            };

            if (editingCuenta) {
                await updateDoc(doc(db, 'inventario_cuentas', editingCuenta.id), docPayload);
                logAuditAction({
                    userId: currentUser?.uid || currentUser?.id,
                    userName: currentUser?.name || currentUser?.email,
                    modulo: 'Inventario',
                    accion: 'Edición de Cuenta',
                    descripcion: `Cuenta editada: ${cuentaCorreo} de ${cuentaServicio}`
                });
                Swal.fire({ title: 'Cuenta Actualizada', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            } else {
                await addDoc(collection(db, 'inventario_cuentas'), {
                    ...docPayload,
                    createdAt: serverTimestamp()
                });
                logAuditAction({
                    userId: currentUser?.uid || currentUser?.id,
                    userName: currentUser?.name || currentUser?.email,
                    modulo: 'Inventario',
                    accion: 'Creación de Cuenta',
                    descripcion: `Cuenta creada: ${cuentaCorreo} de ${cuentaServicio}`
                });

                if (Number(cuentaCostoCompra) > 0) {
                const payload = {
                    tipo: 'gasto',
                    categoria: 'Inversión Inventario',
                    descripcion: `Compra de cuenta ${cuentaCorreo}`,
                    plataforma: cuentaServicio,
                    monto: Number(cuentaCostoCompra),
                    amount: Number(cuentaCostoCompra),
                    origen: 'gestion_cuentas',
                    registrado_por: currentUser?.name || currentUser?.email,
                    created_at: serverTimestamp(),
                    fecha: new Date().getTime()
                };
                await Promise.all([
                    addDoc(collection(db, 'finanzas_movimientos'), payload),
                    addDoc(collection(db, 'finanzas_gastos'), payload)
                ]);
            }
            }

            setShowCuentaModal(false);
            setEditingCuenta(null);
            setCuentaProvId(''); setCuentaServicio('Netflix'); setCuentaCorreo(''); setCuentaVencimiento('');
            setCuentaTipoProducto('Cuenta Completa'); setCuentaEstadoSalud('Disponible'); setCuentaCostoCompra(''); setCuentaNotas('');
            setCuentaCostoProveedor(''); setCuentaPrecioDistribuidor(''); setCuentaPrecioRevendedor(''); setCuentaPrecioClienteFinal('');
            if (!editingCuenta) Swal.fire({ title: 'Cuenta Añadida', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
        } catch (error) {
            Swal.fire('Error', 'No se pudo guardar la cuenta', 'error');
        }
    };

    const handleActionCuenta = async (id, action) => {
        const ref = doc(db, 'inventario_cuentas', id);
        try {
            if (action === 'editar') {
                const c = cuentas.find(item => item.id === id);
                if (c) {
                    setEditingCuenta(c);
                    setCuentaProvId(c.proveedor_id || '');
                    setCuentaServicio(c.servicio || 'Netflix');
                    setCuentaCorreo(c.correo_cuenta || '');
                    setCuentaVencimiento(c.fecha_vencimiento ? new Date(c.fecha_vencimiento).toISOString().split('T')[0] : '');
                    setCuentaTipoProducto(c.tipo_producto || 'Cuenta Completa');
                    setCuentaEstadoSalud(c.estado || 'Disponible');
                    setCuentaCostoProveedor(c.costo_proveedor || '');
                    setCuentaPrecioDistribuidor(c.precio_distribuidor || '');
                    setCuentaPrecioRevendedor(c.precio_revendedor || '');
                    setCuentaPrecioClienteFinal(c.precio_cliente_final || '');
                    setCuentaNotas(c.notas || '');
                    setShowCuentaModal(true);
                }
                return;
            }

            if (action === 'caida' || action === 'pausar') {
                const nuevoEstado = action === 'caida' ? 'Caída' : 'Pausada / Reporte';
                await updateDoc(ref, { estado: nuevoEstado });
                logAuditAction({
                    userId: currentUser?.uid || currentUser?.id,
                    userName: currentUser?.name || currentUser?.email,
                    modulo: 'Inventario',
                    accion: 'Estado de Cuenta',
                    descripcion: `Cuenta ID: ${id} marcada como ${nuevoEstado}`
                });
                Swal.fire({ title: `Cuenta marcada como ${nuevoEstado}`, icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            } else if (action === 'corregir') {
                await updateDoc(ref, { estado: 'Disponible' });
                logAuditAction({
                    userId: currentUser?.uid || currentUser?.id,
                    userName: currentUser?.name || currentUser?.email,
                    modulo: 'Inventario',
                    accion: 'Corrección de Cuenta',
                    descripcion: `Cuenta ID: ${id} marcada como Disponible`
                });
                Swal.fire({ title: 'Cuenta Corregida (Activa)', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            } else if (action === 'reemplazar') {
                const cuenta = cuentas.find(c => c.id === id);
                if (!cuenta) return;

                const { value: nuevoCorreo } = await Swal.fire({
                    title: 'Reemplazar Cuenta',
                    input: 'email',
                    inputLabel: 'Nuevo Correo enviado por el proveedor',
                    inputPlaceholder: 'ejemplo@correo.com',
                    showCancelButton: true,
                    confirmButtonText: 'Reemplazar',
                    cancelButtonText: 'Cancelar'
                });

                if (nuevoCorreo) {
                    await updateDoc(ref, { estado: 'Desechada' });

                    await addDoc(collection(db, 'inventario_cuentas'), {
                        proveedor_id: cuenta.proveedor_id,
                        servicio: cuenta.servicio,
                        correo_cuenta: nuevoCorreo,
                        perfiles_disponibles: cuenta.perfiles_disponibles,
                        fecha_vencimiento: cuenta.fecha_vencimiento,
                        estado: 'Disponible',
                        tipo_producto: cuenta.tipo_producto || 'Cuenta Completa',
                        createdAt: serverTimestamp(),
                        reemplazo_de: cuenta.correo_cuenta
                    });

                    logAuditAction({
                        userId: currentUser?.uid || currentUser?.id,
                        userName: currentUser?.name || currentUser?.email,
                        modulo: 'Inventario',
                        accion: 'Reemplazo de Cuenta',
                        descripcion: `Reemplazada cuenta ${cuenta.correo_cuenta} por ${nuevoCorreo}`
                    });

                    Swal.fire({ title: 'Cuenta Reemplazada', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
                }
            } else if (action === 'renovar') {
                const cuenta = cuentas.find(c => c.id === id);
                if (cuenta) {
                    const { value: costo } = await Swal.fire({
                        title: 'Renovar Cuenta',
                        input: 'text',
                        inputAttributes: { inputmode: 'decimal' },
                        inputLabel: 'Costo de Renovación ($)',
                        inputPlaceholder: 'Ej. 10.000,05',
                        showCancelButton: true,
                        confirmButtonText: 'Renovar',
                        cancelButtonText: 'Cancelar',
                        preConfirm: (value) => {
                            const amount = parseMoney(value);
                            if (amount === null) {
                                Swal.showValidationMessage('Ingresa un monto válido, por ejemplo 1500,50 o 10.000,05.');
                                return false;
                            }
                            return amount;
                        }
                    });
                    
                    if (costo) {
                        const currentVenc = new Date(cuenta.fecha_vencimiento);
                        currentVenc.setMonth(currentVenc.getMonth() + 1);
                        const newVencTime = currentVenc.getTime();
                        
                        // Update cuenta
                        await updateDoc(ref, { fecha_vencimiento: newVencTime, estado: 'Disponible' });
                        
                        // Register in cuentas_renovadas
                        await addDoc(collection(db, 'cuentas_renovadas'), {
                            cuenta_id: id,
                            servicio: cuenta.servicio,
                            correo: cuenta.correo_cuenta,
                            costo: costo,
                            fecha_renovacion: serverTimestamp(),
                            nueva_fecha_vencimiento: newVencTime
                        });

                        // Log automatic financial expense
                        const expensePayload = {
                            tipo: 'gasto',
                            categoria: 'Inversión Inventario',
                            descripcion: `Renovación ${cuenta.servicio} - ${cuenta.correo_cuenta}`,
                            plataforma: cuenta.servicio,
                            monto: costo,
                            amount: costo,
                            origen: 'gestion_cuentas',
                            registrado_por: currentUser?.name || currentUser?.email,
                            created_at: serverTimestamp(),
                            fecha: new Date().getTime()
                        };
                        await Promise.all([
                            addDoc(collection(db, 'finanzas_movimientos'), expensePayload),
                            addDoc(collection(db, 'finanzas_gastos'), expensePayload)
                        ]);

                        logAuditAction({
                            userId: currentUser?.uid || currentUser?.id,
                            userName: currentUser?.name || currentUser?.email,
                            modulo: 'Inventario',
                            accion: 'Renovación de Cuenta',
                            descripcion: `Renovación de ${cuenta.correo_cuenta} por $${costo}`
                        });

                        Swal.fire({ title: 'Renovada (+1 mes)', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
                    }
                }
            } else if (action === 'vender') {
                await updateDoc(ref, { estado: 'Vendida' });
                logAuditAction({
                    userId: currentUser?.uid || currentUser?.id,
                    userName: currentUser?.name || currentUser?.email,
                    modulo: 'Inventario',
                    accion: 'Venta de Cuenta',
                    descripcion: `Cuenta ID: ${id} marcada como Vendida`
                });
                Swal.fire({ title: 'Cuenta Vendida', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            } else if (action === 'eliminar') {
                if (window.confirm('¿Eliminar esta cuenta definitivamente?')) {
                    await deleteDoc(ref);
                    logAuditAction({
                        userId: currentUser?.uid || currentUser?.id,
                        userName: currentUser?.name || currentUser?.email,
                        modulo: 'Inventario',
                        accion: 'Eliminación de Cuenta',
                        descripcion: `Eliminada cuenta ID: ${id}`
                    });
                }
            }
        } catch (error) {
            Swal.fire('Error', 'No se pudo actualizar la cuenta', 'error');
        }
    };

    const handleSaveInlineUnits = async (item) => {
        try {
            if (!item.exists) {
                await addDoc(collection(db, 'almacen_stock'), {
                    plataforma: item.plataforma,
                    unidades: editingValue,
                    costo_promedio: 0,
                    precio_sugerido: 0,
                    proveedor: 'N/A'
                });
            } else {
                await updateDoc(doc(db, 'almacen_stock', item.id), {
                    unidades: editingValue
                });
            }
            setEditingId(null);
            Swal.fire({ title: 'Stock Actualizado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
        } catch (error) {
            Swal.fire('Error', 'No se pudo actualizar el stock', 'error');
        }
    };



    // --- PROVEEDORES LOGIC ---
    const handleSaveProv = async (e) => {
        e.preventDefault();
        try {
            await addDoc(collection(db, 'proveedores'), {
                nombre: provNombre,
                alias: provAlias,
                contacto: provContacto,
                calificacion: 5,
                estado: 'activo'
            });
            setShowProvModal(false);
            setProvNombre(''); setProvAlias(''); setProvContacto('');
            Swal.fire({ title: 'Añadido', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
        } catch (error) {
            Swal.fire('Error', 'No se pudo guardar el proveedor', 'error');
        }
    };

    const handleDeleteProv = async (id) => {
        if(window.confirm('¿Seguro que deseas eliminar este proveedor?')) {
            await deleteDoc(doc(db, 'proveedores', id));
        }
    };

    // --- BITACORA LOGIC ---
    const handleSaveBitacora = async (e) => {
        e.preventDefault();
        try {
            await addDoc(collection(db, 'inventario_bitacora'), {
                accion: bitacoraAccion,
                plataforma: bitacoraPlataforma,
                correo: bitacoraCorreo,
                notas: bitacoraNotas,
                usuario: currentUser.email,
                fecha: serverTimestamp(),
                verificado: false
            });
            setBitacoraAccion('Cuenta Creada'); setBitacoraPlataforma(''); setBitacoraCorreo(''); setBitacoraNotas('');
            Swal.fire({ title: 'Registro Añadido', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
        } catch (error) {
            Swal.fire('Error', 'No se pudo guardar en la bitácora', 'error');
        }
    };

    const handleVerifyBitacora = async (id) => {
        try {
            await updateDoc(doc(db, 'inventario_bitacora', id), {
                verificado: true,
                verificadoPor: currentUser.name || currentUser.email
            });
            Swal.fire({ title: 'Verificado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
        } catch (error) {
            console.error(error);
        }
    };
    const handleEditBitacora = async (b) => {
        const { value: formValues } = await Swal.fire({
            title: 'Editar Entrada de Bitácora',
            html: `
                <div class="mb-3">
                    <label class="block text-sm text-left mb-1">Acción</label>
                    <input id="swal-edit-accion" type="text" class="swal2-input !mt-0" value="${b.accion}" style="width: 100%;">
                </div>
                <div class="mb-3">
                    <label class="block text-sm text-left mb-1">Plataforma</label>
                    <input id="swal-edit-plataforma" type="text" class="swal2-input !mt-0" value="${b.plataforma}" style="width: 100%;">
                </div>
                <div class="mb-3">
                    <label class="block text-sm text-left mb-1">Correo</label>
                    <input id="swal-edit-correo" type="text" class="swal2-input !mt-0" value="${b.correo || ''}" style="width: 100%;">
                </div>
                <div>
                    <label class="block text-sm text-left mb-1">Notas</label>
                    <textarea id="swal-edit-notas" class="swal2-textarea !mt-0" style="width: 100%;">${b.notas || ''}</textarea>
                </div>
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Guardar Cambios',
            cancelButtonText: 'Cancelar',
            preConfirm: () => {
                return {
                    accion: document.getElementById('swal-edit-accion').value,
                    plataforma: document.getElementById('swal-edit-plataforma').value,
                    correo: document.getElementById('swal-edit-correo').value,
                    notas: document.getElementById('swal-edit-notas').value
                }
            }
        });

        if (formValues) {
            try {
                await updateDoc(doc(db, 'inventario_bitacora', b.id), formValues);
                Swal.fire({ title: 'Actualizado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            } catch (err) {
                Swal.fire('Error', 'No se pudo actualizar la bitácora', 'error');
            }
        }
    };
    // --- HELPERS ---
    const getProvName = (id) => {
        const p = proveedores.find(prov => prov.id === id);
        return p ? (p.alias || 'Sin Alias') : 'Desconocido';
    };

    const isExpiringSoon = (timestamp) => {
        if (!timestamp) return false;
        const diff = timestamp - Date.now();
        const days = diff / (1000 * 60 * 60 * 24);
        return days >= 0 && days <= 3;
    };





    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp).toLocaleDateString();
    };

    const sistemaCuentas = sistemaInventory.cuentas.map(cuenta => ({
        id: `sistema-${cuenta.id}`,
        servicio: cuenta.servicio,
        correo_cuenta: cuenta.correo_cuenta,
        fecha_vencimiento: cuenta.fecha_vencimiento,
        tipo_producto: cuenta.tipo_producto,
        estado: cuenta.estado_salud,
        estado_salud: cuenta.estado_salud,
        precio_cliente_final: cuenta.precio_venta,
        notas: cuenta.nota,
        origen: 'Sistema'
    }));
    const cuentasUnificadas = [...cuentas, ...sistemaCuentas];
    const filteredCuentas = cuentasUnificadas
        .filter(c => {
            const matchServicio = filtroServicio === 'Todos' || c.servicio === filtroServicio;
            const matchEstado = filtroEstado === 'Todos' || c.estado?.toUpperCase() === filtroEstado?.toUpperCase();
            const matchNotas = filtroNotas === 'Todos' || (c.notas || '').toLowerCase().includes(filtroNotas.toLowerCase());
            return matchServicio && matchEstado && matchNotas;
        })
        .sort((a, b) => (a.servicio || '').localeCompare(b.servicio || ''));

    const filteredProveedores = proveedores.filter(p => {
        if (!filtroProvServicio) return true;
        return p.alias?.toLowerCase().includes(filtroProvServicio.toLowerCase()) || false;
    });

    const catalogoAlmacen = configServicios.map(servicio => {
        const stockItem = stockAlmacen.find(s => s.plataforma === servicio.nombre);
        const units = stockItem?.unidades || 0;

        return {
            id: stockItem?.id || servicio.nombre,
            plataforma: servicio.nombre,
            unidades: units,
            costo_promedio: stockItem?.costo_promedio || 0,
            precio_sugerido: stockItem?.precio_sugerido || 0,
            proveedor: stockItem?.proveedor || 'N/A',
            color: servicio.color || '#6366f1',
            punto_de_reorden: servicio.punto_de_reorden || 5,
            tipo_producto: servicio.tipo_producto || 'Cuenta Completa',
            exists: !!stockItem
        };
    });

    const stockPerfiles = sistemaInventory.stock_perfiles;
    const stockCuentas = sistemaInventory.stock_cuentas;







    const getMargenStyle = (margen) => {
        if (margen === null || margen === undefined) return '';
        if (margen < 0) return 'bg-red-500/20 text-red-400';
        if (margen <= UMBRAL_RENTABILIDAD) return 'bg-yellow-400/20 text-amber-400';
        return 'text-emerald-400';
    };

    return (
        <div className="min-h-screen text-slate-100 font-sans">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight m-0">Inventario</h1>
                    <p className="text-sm text-slate-400 mt-1">Gestión de stock de streaming y proveedores</p>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {[
                    ['Total cuentas', sistemaInventory.total_cuentas],
                    ['Disponibles', sistemaInventory.cuentas_disponibles],
                    ['Hospital', sistemaInventory.cuentas_hospital],
                    ['Pausadas', sistemaInventory.cuentas_pausadas]
                ].map(([label, value]) => <div key={label} className="bg-slate-800/60 border border-slate-700 rounded-xl p-4"><p className="text-xs uppercase text-slate-400">{label} · Sistema</p><p className="text-2xl font-bold text-white mt-1">{value ?? 0}</p></div>)}
            </div>
                <div className="bg-amber-500/10 border border-amber-500/50 p-4 rounded-xl mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <AlertTriangle className="text-amber-500 flex-shrink-0" size={24} />
                        <div>
                            <span className="text-white font-bold block">Auditoría de Verificación</span>
                            <span className="text-amber-400 text-sm">Pendiente por conciliar: {bitacora.filter(b => !b.verificado).length} movimientos</span>
                        </div>
                    </div>
                    <button onClick={() => setActiveTab('bitacora')} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-xs px-3 py-2 rounded-lg transition-colors flex items-center gap-1">Ver Bitácora</button>
                </div>
            )}

            {/* BLOQUE MÉTRICO ATC */}
            {isATC && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 flex items-center justify-between shadow-lg">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-amber-500/10 rounded-xl text-amber-400">
                                <AlertTriangle size={24} />
                            </div>
                            <div>
                                <span className="text-slate-400 text-xs font-bold uppercase">Cuentas Pausadas / Reportadas</span>
                                <span className="text-2xl font-bold text-white block mt-1 font-mono">{cuentas.filter(c => c.estado === 'Pausada / Reporte').length}</span>
                            </div>
                        </div>
                        {!isATC && <button onClick={() => { setActiveTab('stock'); setFiltroEstado('Pausada / Reporte'); }} className="text-xs text-amber-400 hover:underline">Ver todas</button>}
                    </div>

                    <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 flex items-center justify-between shadow-lg">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-red-500/10 rounded-xl text-red-400">
                                <XCircle size={24} />
                            </div>
                            <div>
                                <span className="text-slate-400 text-xs font-bold uppercase">Cuentas Caídas</span>
                                <span className="text-2xl font-bold text-white block mt-1 font-mono">{cuentas.filter(c => c.estado === 'Caída').length}</span>
                            </div>
                        </div>
                        {!isATC && <button onClick={() => { setActiveTab('stock'); setFiltroEstado('Caída'); }} className="text-xs text-red-400 hover:underline">Ver todas</button>}
                    </div>
                </div>
            )}

            {/* TABS */}
            <div className="flex border-b border-slate-700/50 mb-6 gap-2 overflow-x-auto scrollbar-hide flex-nowrap whitespace-nowrap">
                <button
                    onClick={() => setActiveTab('almacen')}
                    className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'almacen' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}
                >
                    <Package size={18} /> Almacén Operativo
                </button>
                <button
                    onClick={() => setActiveTab('insumos')}
                    className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'insumos' ? 'border-pink-500 text-pink-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}
                >
                    <Archive size={18} /> Insumos
                </button>
                {!isATC && (
                <button
                    onClick={() => setActiveTab('stock')}
                    className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'stock' ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}
                >
                    <Search size={18} /> Stock
                </button>
                )}
                <button
                    onClick={() => setActiveTab('bitacora')}
                    className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'bitacora' ? 'border-purple-500 text-purple-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}
                >
                    <History size={18} /> Bitácora
                </button>
                <button
                    onClick={() => setActiveTab('renovadas')}
                    className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'renovadas' ? 'border-amber-500 text-amber-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}
                >
                    <RotateCw size={18} /> Renovaciones
                </button>
                {isAdminOrGerente && (
                    <button
                        onClick={() => setActiveTab('rentabilidad')}
                        className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'rentabilidad' ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}
                    >
                        <DollarSign size={18} /> Rentabilidad
                    </button>
                )}
                {isAdminOrGerente && (
                    <button
                        onClick={() => setActiveTab('proveedores')}
                        className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'proveedores' ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}
                    >
                        <Users size={18} /> Directorio
                    </button>
                )}
            </div>

            {/* TAB: ALMACÉN DE INSUMOS */}
            {activeTab === 'insumos' && (
                <AlmacenInsumos isATC={isATC} isAdminOrGerente={isAdminOrGerente} />
            )}

            {/* TAB: ALMACEN DIGITAL / ALMACÉN OPERATIVO */}
            {activeTab === 'almacen' && (
                <div className="space-y-8 animate-in fade-in duration-300">
                    <div className="flex justify-end">
                        <button onClick={generarReporteCompras} className="bg-slate-700/80 border border-slate-600/50 hover:bg-slate-700 text-cyan-400 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm shadow">
                            📦 Informe de Compras Necesarias
                        </button>
                    </div>
                    
                    {/* TABLA A: STOCK DE PERFILES */}
                    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
                        <div className="p-5 border-b border-slate-700/50 flex justify-between items-center bg-slate-900/40">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <Package size={20} className="text-cyan-400" /> Stock de Perfiles
                            </h3>
                            <span className="text-xs text-slate-400 font-medium font-mono bg-slate-800 px-3 py-1 rounded-full border border-slate-700">Modo Manual</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-slate-300">
                                <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs">
                                    <tr>
                                        <th className="px-6 py-4">Servicio</th>
                                        <th className="px-6 py-4 text-center">Unidades Disponibles</th>
                                        <th className="px-6 py-4 text-center">Punto de Reorden</th>
                                        <th className="px-6 py-4 text-center">Estado</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/50">
                                    {stockPerfiles.length === 0 ? (
                                        <tr><td colSpan="4" className="text-center py-8 text-slate-500">No hay servicios de perfil registrados</td></tr>
                                    ) : stockPerfiles.map(item => {
                                        const isLowStock = item.unidades <= item.punto_de_reorden;
                                        return (
                                            <tr key={item.id} className={`hover:bg-slate-700/30 ${isLowStock ? 'bg-red-900/20' : ''}`}>
                                                <td className="px-6 py-4 font-bold flex items-center gap-2">
                                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                                                    <span className="text-white">{item.plataforma}</span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    {editingId === item.id ? (
                                                        <div className="flex items-center justify-center gap-1">
                                                            <input 
                                                                type="number" 
                                                                value={editingValue} 
                                                                onChange={(e) => setEditingValue(Number(e.target.value))} 
                                                                className="w-20 bg-slate-900 text-white font-mono font-bold px-2 py-1 border border-indigo-500 rounded focus:outline-none text-center"
                                                            />
                                                            <button onClick={() => handleSaveInlineUnits(item)} title="Guardar" className="text-emerald-400 hover:text-emerald-300 p-1"><CheckCircle size={18} /></button>
                                                            <button onClick={() => setEditingId(null)} title="Cancelar" className="text-slate-400 hover:text-slate-300 p-1"><XCircle size={18} /></button>
                                                        </div>
                                                    ) : (
                                                        <span 
                                                            onClick={() => {
                                                                if (!isATC) {
                                                                    setEditingId(item.id);
                                                                    setEditingValue(item.unidades);
                                                                }
                                                            }} 
                                                            title={!isATC ? "Haga clic para editar unidades" : ""}
                                                            className={`px-3 py-1 rounded-full font-bold font-mono ${!isATC ? 'cursor-pointer hover:bg-slate-700' : ''} ${isLowStock ? 'bg-red-500/20 text-red-400 border border-red-500/50' : 'bg-slate-800 text-white border border-slate-600'}`}
                                                        >
                                                            {item.unidades}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-center font-mono">{item.punto_de_reorden}</td>
                                                <td className="px-6 py-4 text-center">
                                                    {isLowStock ? (
                                                        <span className="flex items-center justify-center gap-2 text-red-400 font-bold text-xs" title="Es necesario comprar/cargar stock">
                                                            <AlertTriangle size={16} className="text-red-500 animate-pulse" /> 
                                                            <span>Es necesario comprar/cargar stock</span>
                                                        </span>
                                                    ) : (
                                                        <span className="text-emerald-400 font-bold text-xs">Óptimo</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* TABLA B: STOCK DE CUENTAS COMPLETAS */}
                    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
                        <div className="p-5 border-b border-slate-700/50 flex justify-between items-center bg-slate-900/40">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <Package size={20} className="text-indigo-400" /> Stock de Cuentas Completas
                            </h3>
                            <span className="text-xs text-slate-400 font-medium font-mono bg-slate-800 px-3 py-1 rounded-full border border-slate-700">Modo Manual</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-slate-300">
                                <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs">
                                    <tr>
                                        <th className="px-6 py-4">Servicio</th>
                                        <th className="px-6 py-4 text-center">Unidades Disponibles</th>
                                        <th className="px-6 py-4 text-center">Punto de Reorden</th>
                                        <th className="px-6 py-4 text-center">Estado</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/50">
                                    {stockCuentas.length === 0 ? (
                                        <tr><td colSpan="4" className="text-center py-8 text-slate-500">No hay servicios completos registrados</td></tr>
                                    ) : stockCuentas.map(item => {
                                        const isLowStock = item.unidades <= item.punto_de_reorden;
                                        return (
                                            <tr key={item.id} className={`hover:bg-slate-700/30 ${isLowStock ? 'bg-red-900/20' : ''}`}>
                                                <td className="px-6 py-4 font-bold flex items-center gap-2">
                                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                                                    <span className="text-white">{item.plataforma}</span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    {editingId === item.id ? (
                                                        <div className="flex items-center justify-center gap-1">
                                                            <input 
                                                                type="number" 
                                                                value={editingValue} 
                                                                onChange={(e) => setEditingValue(Number(e.target.value))} 
                                                                className="w-20 bg-slate-900 text-white font-mono font-bold px-2 py-1 border border-indigo-500 rounded focus:outline-none text-center"
                                                            />
                                                            <button onClick={() => handleSaveInlineUnits(item)} title="Guardar" className="text-emerald-400 hover:text-emerald-300 p-1"><CheckCircle size={18} /></button>
                                                            <button onClick={() => setEditingId(null)} title="Cancelar" className="text-slate-400 hover:text-slate-300 p-1"><XCircle size={18} /></button>
                                                        </div>
                                                    ) : (
                                                        <span 
                                                            onClick={() => {
                                                                if (!isATC) {
                                                                    setEditingId(item.id);
                                                                    setEditingValue(item.unidades);
                                                                }
                                                            }} 
                                                            title={!isATC ? "Haga clic para editar unidades" : ""}
                                                            className={`px-3 py-1 rounded-full font-bold font-mono ${!isATC ? 'cursor-pointer hover:bg-slate-700' : ''} ${isLowStock ? 'bg-red-500/20 text-red-400 border border-red-500/50' : 'bg-slate-800 text-white border border-slate-600'}`}
                                                        >
                                                            {item.unidades}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-center font-mono">{item.punto_de_reorden}</td>
                                                <td className="px-6 py-4 text-center">
                                                    {isLowStock ? (
                                                        <span className="flex items-center justify-center gap-2 text-red-400 font-bold text-xs" title="Es necesario comprar/cargar stock">
                                                            <AlertTriangle size={16} className="text-red-500 animate-pulse" /> 
                                                            <span>Es necesario comprar/cargar stock</span>
                                                        </span>
                                                    ) : (
                                                        <span className="text-emerald-400 font-bold text-xs">Óptimo</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            )}

            {/* TAB: STOCK DE CUENTAS */}
            {activeTab === 'stock' && (
                <div className="space-y-6 animate-in fade-in duration-300">
                    <div className="flex flex-col md:flex-row justify-between gap-4 bg-slate-800/80 p-4 rounded-xl border border-slate-700">
                        <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
                            <select value={filtroServicio} onChange={(e) => setFiltroServicio(e.target.value)} className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500">
                                <option value="Todos">Todos los Servicios</option>
                                {configServicios.length > 0 
                                    ? configServicios.map(s => <option key={s.id} value={s.nombre}>{s.nombre}</option>)
                                    : serviciosList.map(s => <option key={s} value={s}>{s}</option>)
                                }
                            </select>
                            <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500">
                                <option value="Todos">Todos los Estados</option>
                                {estadosCuenta.map(e => <option key={e} value={e}>{e}</option>)}
                            </select>
                            <input 
                                type="text" 
                                value={filtroNotas === 'Todos' ? '' : filtroNotas} 
                                onChange={(e) => setFiltroNotas(e.target.value || 'Todos')} 
                                className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-indigo-500" 
                                placeholder="Filtrar por Notas..." 
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={generarReporteRenovaciones} className="bg-slate-700/80 border border-slate-600/50 hover:bg-slate-700 text-amber-400 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm shadow">
                                📄 Generar Informe de Renovaciones
                            </button>
                            {!isATC && (
                            <button onClick={() => setShowCuentaModal(true)} className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm">
                                <PlusCircle size={18} /> Agregar Cuenta
                            </button>
                            )}
                        </div>
                    </div>

                    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
                        <div className="overflow-x-auto scrollbar-hide">
                            <table className="w-full text-sm text-left text-slate-300">
                                <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs">
                                    <tr>
                                        <th className="px-6 py-4">Servicio</th>
                                        <th className="px-6 py-4">Correo</th>
                                        <th className="px-6 py-4">Proveedor</th>
                                        <th className="px-6 py-4 text-center">Vencimiento</th>
                                        <th className="px-6 py-4 text-center">Estado</th>
                                        <th className="px-6 py-4">Notas</th>
                                        {!isATC && <th className="px-6 py-4 text-center">Acciones</th>}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/50">
                                    {filteredCuentas.length === 0 ? (
                                        <tr><td colSpan="8" className="text-center py-8 text-slate-500">No hay cuentas que coincidan</td></tr>
                                    ) : filteredCuentas.map(cuenta => {
                                        const expiring = isExpiringSoon(cuenta.fecha_vencimiento);
                                        const isCaida = cuenta.estado === 'Caída';
                                        return (
                                            <tr key={cuenta.id} className={`hover:bg-slate-700/30 ${isCaida ? 'bg-red-900/10' : ''}`}>
                                                <td className="px-6 py-4 font-bold text-white">{cuenta.servicio}</td>
                                                <td className="px-6 py-4">{cuenta.correo_cuenta}</td>
                                                <td className="px-6 py-4 font-medium">{getProvName(cuenta.proveedor_id)}</td>
                                                <td className={`px-6 py-4 text-center font-medium ${expiring ? 'text-amber-500 flex items-center justify-center gap-2' : ''}`}>
                                                    {expiring && <AlertTriangle size={14} />} {formatDate(cuenta.fecha_vencimiento)}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                                                        cuenta.estado === 'Disponible' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                                                        cuenta.estado === 'Caída' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                                                        cuenta.estado === 'Vencida' ? 'bg-slate-500/10 text-slate-400 border border-slate-700/50' :
                                                        cuenta.estado === 'Pausada / Reporte' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                                                        'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'
                                                    }`}>
                                                        {cuenta.estado}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-slate-400 text-xs truncate max-w-[150px]" title={cuenta.notas}>{cuenta.notas || '-'}</td>
                                                {!isATC && (
                                                <td className="px-6 py-4 text-center">
                                                    <div className="flex justify-center gap-2">
                                                        <button onClick={() => handleActionCuenta(cuenta.id, 'editar')} title="Editar" className="text-slate-400 hover:text-cyan-400 p-1"><Edit2 size={16} /></button>
                                                        <button onClick={() => handleActionCuenta(cuenta.id, 'renovar')} title="Renovar" className="text-slate-400 hover:text-emerald-400 p-1"><RotateCw size={16} /></button>
                                                        <button onClick={() => handleActionCuenta(cuenta.id, 'eliminar')} title="Eliminar" className="text-slate-500 hover:text-red-500 p-1"><Trash2 size={16} /></button>
                                                    </div>
                                                </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}



            {/* TAB: RENTABILIDAD Y COSTOS (EXCEL MIRROR) */}
            {activeTab === 'rentabilidad' && isAdminOrGerente && (
                <div className="space-y-6 animate-in fade-in duration-300">
                    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
                        <div className="p-5 border-b border-slate-700/50 flex justify-between items-center bg-slate-900/40">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <TrendingUp size={20} className="text-emerald-400" /> Estructura de Costos y Rentabilidad
                            </h3>
                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest bg-slate-800 px-3 py-1 rounded-full border border-slate-700">Mirror Excel Hoprin+</span>
                        </div>
                        <div className="overflow-x-auto scrollbar-hide">
                            <table className="w-full text-xs text-left text-slate-300 whitespace-nowrap">
                                <thead className="bg-slate-900/50 text-slate-400 uppercase text-[10px]">
                                    <tr>
                                        <th className="px-4 py-3">ID / Descripción</th>
                                        <th className="px-4 py-3">Plataforma</th>
                                        <th className="px-4 py-3 text-center">Costo Base ($)</th>
                                        <th className="px-4 py-3 text-center">Asociados (35%)</th>
                                        <th className="px-4 py-3 text-center font-bold text-cyan-400">Costo Total ($)</th>
                                        <th className="px-4 py-3 text-center">Precio Dist.</th>
                                        <th className="px-4 py-3 text-center">Precio Rev.</th>
                                        <th className="px-4 py-3 text-center">Precio Cliente</th>
                                        <th className="px-4 py-3 text-center">MB Dist. (%)</th>
                                        <th className="px-4 py-3 text-center">MB Rev. (%)</th>
                                        <th className="px-4 py-3 text-center">MB Cliente (%)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/50">
                                    {filteredCuentas.length === 0 ? (
                                        <tr><td colSpan="11" className="text-center py-8 text-slate-500">No hay datos disponibles</td></tr>
                                    ) : filteredCuentas.map(cuenta => {
                                        const cBase = Number(cuenta.costo_proveedor || 0);
                                        const cAsoc = cBase * 0.35;
                                        const cTotal = cBase + cAsoc;

                                        const pDist = Number(cuenta.precio_distribuidor || 0);
                                        const pRev = Number(cuenta.precio_revendedor || 0);
                                        const pCli = Number(cuenta.precio_cliente_final || 0);

                                        const mbDist = pDist > 0 ? (pDist - cTotal) / pDist : 0;
                                        const mbRev = pRev > 0 ? (pRev - cTotal) / pRev : 0;
                                        const mbCli = pCli > 0 ? (pCli - cTotal) / pCli : 0;

                                        return (
                                            <tr key={cuenta.id} className="hover:bg-slate-700/30 border-b border-slate-700/50 transition-colors">
                                                <td className="px-4 py-3">
                                                    <div className="flex flex-col">
                                                        <span className="text-white font-bold">{cuenta.servicio}</span>
                                                        <span className="text-slate-500 text-[10px] truncate max-w-[150px]">{cuenta.correo_cuenta}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 font-medium">{getProvName(cuenta.proveedor_id)}</td>
                                                <td className="px-4 py-3 text-center font-mono">${cBase.toFixed(2)}</td>
                                                <td className="px-4 py-3 text-center font-mono text-slate-400">${cAsoc.toFixed(2)}</td>
                                                <td className="px-4 py-3 text-center font-mono font-bold text-cyan-400 bg-cyan-500/5">${cTotal.toFixed(2)}</td>
                                                <td className="px-4 py-3 text-center font-mono">${pDist.toFixed(2)}</td>
                                                <td className="px-4 py-3 text-center font-mono">${pRev.toFixed(2)}</td>
                                                <td className="px-4 py-3 text-center font-mono">${pCli.toFixed(2)}</td>
                                                
                                                <td className={`px-4 py-3 text-center font-mono font-bold ${getMargenStyle(mbDist)}`}>
                                                    {(mbDist * 100).toFixed(1)}%
                                                </td>
                                                <td className={`px-4 py-3 text-center font-mono font-bold ${getMargenStyle(mbRev)}`}>
                                                    {(mbRev * 100).toFixed(1)}%
                                                </td>
                                                <td className={`px-4 py-3 text-center font-mono font-bold ${getMargenStyle(mbCli)}`}>
                                                    {(mbCli * 100).toFixed(1)}%
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}


            {/* TAB: CUENTAS RENOVADAS */}
            {activeTab === 'renovadas' && (
                <div className="space-y-6 animate-in fade-in duration-300">
                    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
                        <div className="p-5 border-b border-slate-700/50"><h3 className="text-lg font-bold text-white">Historial de Cuentas Renovadas</h3></div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-slate-300">
                                <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs">
                                    <tr>
                                        <th className="px-6 py-4">Fecha de Renovación</th>
                                        <th className="px-6 py-4">Servicio</th>
                                        <th className="px-6 py-4">Correo</th>
                                        <th className="px-6 py-4">Costo de Renovación</th>
                                        <th className="px-6 py-4">Nueva Fecha Vencimiento</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/50">
                                    {cuentasRenovadas.length === 0 ? (
                                        <tr><td colSpan="5" className="text-center py-8 text-slate-500">No hay renovaciones registradas</td></tr>
                                    ) : cuentasRenovadas.map(ren => (
                                        <tr key={ren.id} className="hover:bg-slate-700/30">
                                            <td className="px-6 py-4 text-slate-400 text-xs">{formatDate(ren.fecha_renovacion)}</td>
                                            <td className="px-6 py-4 font-bold text-white">{ren.servicio}</td>
                                            <td className="px-6 py-4 text-slate-300">{ren.correo}</td>
                                            <td className="px-6 py-4 font-mono text-amber-400">${Number(ren.costo || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                            <td className="px-6 py-4 font-medium text-emerald-400">{formatDate(ren.nueva_fecha_vencimiento)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
            {/* TAB: BITÁCORA */}
            {activeTab === 'bitacora' && (
                <div className="space-y-6 animate-in fade-in duration-300">
                    {!isATC && (
                    <div className="bg-slate-800/80 p-6 rounded-2xl border border-slate-700/50 shadow-xl">
                        <h3 className="text-lg font-bold text-white mb-4">Nueva Entrada en Bitácora</h3>
                        <form onSubmit={handleSaveBitacora} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Acción</label>
                                <select value={bitacoraAccion} onChange={e => setBitacoraAccion(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none">
                                    <option>Cuenta Creada</option>
                                    <option>Cuenta Renovada</option>
                                    <option>Problema Reportado</option>
                                    <option>Otra</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Plataforma</label>
                                <input required type="text" value={bitacoraPlataforma} onChange={e => setBitacoraPlataforma(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none" placeholder="Ej: Netflix" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Correo (Opcional)</label>
                                <input type="email" value={bitacoraCorreo} onChange={e => setBitacoraCorreo(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none" placeholder="correo@ejemplo.com" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Notas</label>
                                <input required type="text" value={bitacoraNotas} onChange={e => setBitacoraNotas(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none" placeholder="Detalles..." />
                            </div>
                            <button type="submit" className="md:col-span-4 bg-purple-600 hover:bg-purple-500 text-white px-6 py-2 rounded-lg font-medium transition-colors">Guardar Registro</button>
                        </form>
                    </div>
                    )}

                    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
                        <div className="p-5 border-b border-slate-700/50"><h3 className="text-lg font-bold text-white">Historial Operativo</h3></div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-slate-300">
                                <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs">
                                    <tr>
                                        <th className="px-6 py-4">Fecha</th>
                                        <th className="px-6 py-4">Usuario</th>
                                        <th className="px-6 py-4">Acción</th>
                                        <th className="px-6 py-4">Plataforma</th>
                                        <th className="px-6 py-4">Correo / Notas</th>
                                        <th className="px-6 py-4 text-center">Verificación</th>
                                        {userRole === 'ADMIN' && <th className="px-6 py-4 text-center">Acción</th>}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/50">
                                    {bitacora.length === 0 ? (
                                        <tr><td colSpan="7" className="text-center py-8 text-slate-500">No hay registros recientes</td></tr>
                                    ) : bitacora.map(b => (
                                        <tr key={b.id} className="hover:bg-slate-700/30">
                                            <td className="px-6 py-4 text-slate-400 text-xs">{formatDate(b.fecha?.toMillis())}</td>
                                            <td className="px-6 py-4 font-medium text-white">{b.usuario}</td>
                                            <td className="px-6 py-4"><span className="bg-slate-900 text-purple-400 px-2 py-1 rounded-md text-xs font-bold uppercase">{b.accion}</span></td>
                                            <td className="px-6 py-4 font-bold">{b.plataforma}</td>
                                            <td className="px-6 py-4">
                                                {b.correo && b.correo !== 'N/A' && <div className="text-slate-300 mb-1">{b.correo}</div>}
                                                <div className="text-slate-400 italic text-xs">{b.notas}</div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                {b.verificado ? (
                                                    <span className="flex items-center justify-center gap-1 text-emerald-500 font-bold text-xs bg-emerald-500/10 px-2 py-1 rounded w-fit mx-auto"><CheckCircle size={14}/> Verificado</span>
                                                ) : (
                                                    (isAdminOrGerente || userRole.includes('SUPERVISOR')) ? (
                                                        <button onClick={() => handleVerifyBitacora(b.id)} className="flex items-center justify-center gap-1 bg-indigo-600 hover:bg-indigo-500 text-white px-2 py-1 rounded text-xs transition-colors mx-auto font-bold"><CheckCircle size={14}/> Verificar</button>
                                                    ) : (
                                                        <span className="flex items-center justify-center text-amber-500 font-bold text-xs bg-amber-500/10 px-2 py-1 rounded w-fit mx-auto">Pendiente</span>
                                                    )
                                                )}
                                            </td>
                                            {userRole === 'ADMIN' && (
                                                <td className="px-6 py-4 text-center">
                                                    <button onClick={() => handleEditBitacora(b)} title="Editar Entrada" className="flex items-center justify-center gap-1 bg-slate-800 hover:bg-slate-700 text-cyan-400 border border-slate-700/50 hover:border-cyan-500/50 px-3 py-1.5 rounded text-xs transition-colors mx-auto font-bold shadow-sm">
                                                        <Edit2 size={14}/> Editar
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB: PROVEEDORES (ADMIN/GERENTE ONLY) */}
            {activeTab === 'proveedores' && isAdminOrGerente && (
                <div className="space-y-6 animate-in fade-in duration-300">
                    <div className="flex flex-col md:flex-row justify-between bg-slate-800/80 p-4 rounded-xl border border-slate-700 mb-6 gap-4">
                        <div className="flex items-center gap-2 flex-1 max-w-md bg-slate-900 border border-slate-700 rounded-lg px-4 py-2">
                            <Search size={18} className="text-slate-500" />
                            <input 
                                type="text" 
                                placeholder="Filtrar por Alias..." 
                                value={filtroProvServicio}
                                onChange={(e) => setFiltroProvServicio(e.target.value)}
                                className="bg-transparent text-white focus:outline-none w-full"
                            />
                        </div>
                        <button onClick={() => setShowProvModal(true)} className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap">
                            <PlusCircle size={18} /> Nuevo Proveedor
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredProveedores.map(prov => (
                            <div key={prov.id} className="bg-slate-800 rounded-xl p-5 border border-slate-700 hover:border-emerald-500/50 transition-colors relative group">
                                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleDeleteProv(prov.id)} className="text-slate-500 hover:text-red-400"><Trash2 size={16} /></button>
                                </div>
                                <h3 className="text-xl font-bold text-white mb-1">{prov.nombre}</h3>
                                <p className="text-slate-400 text-sm mb-4">{prov.contacto}</p>
                                <div className="flex justify-between items-center text-xs border-t border-slate-700 pt-3">
                                    <span className="bg-emerald-500/10 text-emerald-400 px-2 py-1 rounded-md font-bold uppercase">Alias: {prov.alias}</span>
                                </div>
                            </div>
                        ))}
                        {filteredProveedores.length === 0 && <div className="col-span-full text-center text-slate-500 py-8">No hay proveedores que coincidan.</div>}
                    </div>
                </div>
            )}
            {/* MODAL: NUEVA CUENTA */}
            {showCuentaModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700 shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="p-5 border-b border-slate-700/50 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-white">{editingCuenta ? 'Editar Cuenta' : 'Registrar Nueva Cuenta'}</h3>
                            <button onClick={() => { setShowCuentaModal(false); setEditingCuenta(null); }} className="text-slate-400 hover:text-white"><XCircle size={24} /></button>
                        </div>
                        <form onSubmit={handleSaveCuenta} className="p-5 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Proveedor</label>
                                <select required value={cuentaProvId} onChange={(e) => setCuentaProvId(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none">
                                    <option value="" disabled>Seleccione un proveedor</option>
                                    {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Servicio</label>
                                <select required value={cuentaServicio} onChange={(e) => setCuentaServicio(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none">
                                    {configServicios.length > 0
                                        ? configServicios.map(s => <option key={s.id} value={s.nombre}>{s.nombre}</option>)
                                        : serviciosList.map(s => <option key={s} value={s}>{s}</option>)
                                    }
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-1">Costo de Compra ($)</label>
                                    <input type="number" step="0.01" value={cuentaCostoCompra} onChange={(e) => setCuentaCostoCompra(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none" placeholder="0.00" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-1">Correo de la Cuenta</label>
                                    <input required type="email" value={cuentaCorreo} onChange={(e) => setCuentaCorreo(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none" placeholder="ejemplo@correo.com" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Vencimiento</label>
                                <input required type="date" value={cuentaVencimiento} onChange={(e) => setCuentaVencimiento(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none [color-scheme:dark]" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-1">Categoría</label>
                                    <select value={cuentaTipoProducto} onChange={(e) => setCuentaTipoProducto(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white focus:border-indigo-500 focus:outline-none text-xs">
                                        <option value="Cuenta Completa">Cuenta Completa</option>
                                        <option value="Cuenta para Perfiles">Cuenta para Perfiles</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-1">Estado de Salud</label>
                                    <select value={cuentaEstadoSalud} onChange={(e) => setCuentaEstadoSalud(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white focus:border-indigo-500 focus:outline-none text-xs">
                                        <option value="Disponible">ACTIVA / DISPONIBLE</option>
                                        <option value="Pausada / Reporte">PAUSADA / REPORTE</option>
                                        <option value="Caída">CAÍDA / SIN SUSCRIPCIÓN</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-1">Costo Proveedor ($)</label>
                                    <input type="number" step="0.01" value={cuentaCostoProveedor} onChange={(e) => setCuentaCostoProveedor(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none" placeholder="Costo base" />
                                </div>
                                <div className="bg-slate-900/50 p-2 rounded-lg border border-slate-700/50">
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase">Costo Total (+35%)</label>
                                    <div className="text-lg font-mono font-bold text-cyan-400">${((Number(cuentaCostoProveedor) || 0) * 1.35).toFixed(2)}</div>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1 uppercase">P. Dist ($)</label>
                                    <input type="number" step="0.01" value={cuentaPrecioDistribuidor} onChange={(e) => setCuentaPrecioDistribuidor(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white focus:border-indigo-500 focus:outline-none" placeholder="0.00" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1 uppercase">P. Rev ($)</label>
                                    <input type="number" step="0.01" value={cuentaPrecioRevendedor} onChange={(e) => setCuentaPrecioRevendedor(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white focus:border-indigo-500 focus:outline-none" placeholder="0.00" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1 uppercase">P. Final ($)</label>
                                    <input type="number" step="0.01" value={cuentaPrecioClienteFinal} onChange={(e) => setCuentaPrecioClienteFinal(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white focus:border-indigo-500 focus:outline-none" placeholder="0.00" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Notas Internas</label>
                                <textarea value={cuentaNotas} onChange={(e) => setCuentaNotas(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-indigo-500 focus:outline-none text-sm h-16 resize-none" placeholder="Detalles..."></textarea>
                            </div>
                            <div className="pt-4 flex gap-3">
                                <button type="button" onClick={() => { setShowCuentaModal(false); setEditingCuenta(null); }} className="flex-1 px-4 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors">Cancelar</button>
                                <button type="submit" className="flex-1 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors">Guardar Cuenta</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: NUEVO PROVEEDOR */}
            {showProvModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-slate-800 rounded-2xl w-full max-w-sm border border-slate-700 shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="p-5 border-b border-slate-700/50 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-white">Nuevo Proveedor</h3>
                            <button onClick={() => setShowProvModal(false)} className="text-slate-400 hover:text-white"><XCircle size={24} /></button>
                        </div>
                        <form onSubmit={handleSaveProv} className="p-5 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Nombre Real</label>
                                <input required type="text" value={provNombre} onChange={(e) => setProvNombre(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" placeholder="Ej: John Doe" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Alias (Visible al Equipo)</label>
                                <input required type="text" value={provAlias} onChange={(e) => setProvAlias(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" placeholder="Ej: PRV-1" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Contacto</label>
                                <input type="text" value={provContacto} onChange={(e) => setProvContacto(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" placeholder="WhatsApp / Telegram" />
                            </div>
                            <div className="pt-4 flex gap-3">
                                <button type="button" onClick={() => setShowProvModal(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors">Cancelar</button>
                                <button type="submit" className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-colors">Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: MÁRGENES */}
            <MarginModal 
                isOpen={showMarginModal} 
                onClose={() => { setShowMarginModal(false); setSelectedMarginData(null); }} 
                data={selectedMarginData} 
                threshold={UMBRAL_RENTABILIDAD}
            />

        </div>
    );
};

export default InventoryDashboard;
